import {
  AcademicTitle,
  PhoneType,
  Salutation,
  UserRole,
  UserType,
} from '@prisma/client'

/**
 * Who an account is, and how the app says their name.
 *
 * One module because the two questions are one: which fields an account may
 * carry decides what there is to address it by, and a rule written twice is a
 * form that accepts what the endpoint refuses.
 */

/** The shape every rule here reads. Whatever else a caller holds — a DTO, a
 *  Prisma row, a half-filled form — it has these. */
export type UserIdentity = {
  type: UserType
  lastName: string
  firstName?: string | null
  salutation?: Salutation | null
  academicTitle?: AcademicTitle | null
}

/**
 * The columns an identity is written from.
 *
 * One list, because three things are keyed by it: the Prisma select every read
 * of a name uses, the check that decides whether a patch has to be validated,
 * and the fields a self-service edit may send. Kept apart they disagree
 * silently and a rule stops firing.
 */
export const USER_IDENTITY_KEYS = [
  'type',
  'lastName',
  'firstName',
  'salutation',
  'academicTitle',
] as const satisfies readonly (keyof UserIdentity)[]

/**
 * The salutations that address more than one person.
 *
 * Neither a first name nor an academic title belongs beside them: "Familie Dr.
 * Anna Müller" names one person and a family in the same breath.
 */
export const PLURAL_SALUTATIONS = [
  Salutation.FAMILIE,
  Salutation.EHELEUTE,
] as const

/** Staff are addressed as one person, so a role other than CUSTOMER carries
 *  neither the plural salutations nor an organisation's shape. */
export const STAFF_SALUTATIONS = [Salutation.HERR, Salutation.FRAU] as const

export const isPluralSalutation = (salutation: Salutation | null | undefined) =>
  !!salutation &&
  (PLURAL_SALUTATIONS as readonly Salutation[]).includes(salutation)

/** Whether a role set is a customer's. Only a customer may be an organisation,
 *  and only a customer is addressed as a family. */
export const isCustomerRoles = (roles: readonly UserRole[]) =>
  roles.includes(UserRole.CUSTOMER)

/** Which salutations an account of this role set may carry. */
export const salutationsFor = (
  roles: readonly UserRole[],
): readonly Salutation[] =>
  isCustomerRoles(roles)
    ? [...STAFF_SALUTATIONS, ...PLURAL_SALUTATIONS]
    : STAFF_SALUTATIONS

/** Which types an account of this role set may be. */
export const typesFor = (roles: readonly UserRole[]): readonly UserType[] =>
  isCustomerRoles(roles)
    ? [UserType.PERSON, UserType.ORGANISATION]
    : [UserType.PERSON]

/**
 * Which of an account's numbers a HubSpot sync owns.
 *
 * Stated here because both sides of the sync need the same answer: the endpoint
 * replaces the number of this kind, and the dialog that asks for confirmation
 * shows the stored one of that kind so the operator sees what confirming
 * overwrites.
 */
export const HUBSPOT_CUSTOMER_PHONE_TYPE = PhoneType.PRIVAT

/** `hs_main_phone` is the number at work. */
export const HUBSPOT_ADVISOR_PHONE_TYPE = PhoneType.ARBEIT

/**
 * Why an identity cannot be stored. Null where it can.
 *
 * The members are the error codes themselves, so the endpoint throws one and
 * the form looks one up without either assembling a string the other has to
 * take apart.
 */
export type UserIdentityError =
  | 'USER_LAST_NAME_REQUIRED'
  | 'USER_ORGANISATION_HAS_NO_PERSON_FIELDS'
  | 'USER_ORGANISATION_NOT_ALLOWED'
  | 'USER_PLURAL_SALUTATION_NOT_ALLOWED'
  | 'USER_PLURAL_SALUTATION_HAS_NO_FIRST_NAME'
  | 'USER_SALUTATION_REQUIRED_WITHOUT_FIRST_NAME'

/** Whether a value carries anything but whitespace. A guard, so a caller that
 *  passes it keeps the narrowed type. */
export const filled = (value: string | null | undefined): value is string =>
  !!value?.trim()

/**
 * Whether an account may be stored as it stands.
 *
 * The rules in one place because they hold on both sides of the wire and for
 * every role: the form draws what this allows and the endpoint refuses what it
 * does not, so a customer created through the API is the customer the screen
 * would have made.
 */
export function userIdentityError(
  identity: UserIdentity,
  roles: readonly UserRole[],
): UserIdentityError | null {
  const isCustomer = isCustomerRoles(roles)

  if (!filled(identity.lastName)) return 'USER_LAST_NAME_REQUIRED'

  if (identity.type === UserType.ORGANISATION) {
    if (!isCustomer) return 'USER_ORGANISATION_NOT_ALLOWED'
    // An organisation is a name and nothing else: it is not a person, so
    // there is nobody for a salutation or a first name to belong to.
    if (
      filled(identity.firstName) ||
      filled(identity.academicTitle) ||
      identity.salutation
    ) {
      return 'USER_ORGANISATION_HAS_NO_PERSON_FIELDS'
    }
    return null
  }

  if (isPluralSalutation(identity.salutation)) {
    if (!isCustomer) return 'USER_PLURAL_SALUTATION_NOT_ALLOWED'
    if (filled(identity.firstName) || filled(identity.academicTitle)) {
      return 'USER_PLURAL_SALUTATION_HAS_NO_FIRST_NAME'
    }
    return null
  }

  // Without a first name the greeting falls back to the salutation and the
  // family name, so there has to be one — otherwise the app would have no way
  // of addressing the account at all.
  if (!filled(identity.firstName) && !identity.salutation) {
    return 'USER_SALUTATION_REQUIRED_WITHOUT_FIRST_NAME'
  }

  return null
}
